/**
 * Account hooks using React Query
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountsService } from '@/services/accounts';
import type { AccountWithConnection, AccountVisibilityTarget } from '@/services/accounts';
import { connectionKeys } from '@/hooks/useConnections';
import type { ManualAccountCreateRequest, ManualAccountUpdateRequest, BalanceEntryCreateRequest, AccountMergeResponse } from '@/types';

// Query keys for cache management
export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...accountKeys.lists(), filters] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: string) => [...accountKeys.details(), id] as const,
  balanceSummary: () => [...accountKeys.all, 'balance-summary'] as const,
  balanceHistory: (id: string) => [...accountKeys.all, 'balance-history', id] as const,
  duplicates: () => [...accountKeys.all, 'duplicates'] as const,
};

export interface UseAccountsOptions {
  includeClosedAccounts?: boolean;
  /**
   * Include hidden accounts in the result (WP-BUG-62).
   * Hidden accounts are excluded by default, matching the backend default.
   */
  includeHidden?: boolean;
}

/**
 * Build the query params for the accounts list.
 *
 * Shared by useAccounts and useAccountsMeta so both resolve to the SAME
 * query key and therefore the same cache entry - the metadata hook must not
 * cause a second network request.
 */
function buildAccountsParams(options?: UseAccountsOptions) {
  return {
    ...(options?.includeClosedAccounts ? {} : { status: 'active' as const }),
    ...(options?.includeHidden ? { includeHidden: true } : {}),
  };
}

/**
 * Hook to fetch all accounts
 * By default only fetches active accounts (excludes closed/inactive)
 * and excludes hidden accounts.
 */
export function useAccounts(options?: UseAccountsOptions) {
  const params = buildAccountsParams(options);
  return useQuery({
    queryKey: accountKeys.list(params),
    queryFn: () => accountsService.getAllWithMeta(params),
    // Project to the accounts array so every existing consumer keeps
    // receiving `data` as an array (WP-BUG-62 kept this shape deliberately).
    select: (result) => result.accounts,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to fetch accounts list metadata (total, hiddenCount).
 *
 * WP-BUG-62: shares the exact query key and queryFn with useAccounts, so this
 * reads from the same cache entry rather than issuing a second request. Used
 * to show "Show hidden accounts (N)" when hiddenCount > 0.
 */
export function useAccountsMeta(options?: UseAccountsOptions) {
  const params = buildAccountsParams(options);
  return useQuery({
    queryKey: accountKeys.list(params),
    queryFn: () => accountsService.getAllWithMeta(params),
    select: (result) => ({ total: result.total, hiddenCount: result.hiddenCount }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch suspected duplicate account groups (WP-BUG-63).
 *
 * Re-linking an institution issues new provider account IDs, which can create
 * a second account row for the same real-world account. Surfacing these lets
 * the user merge them deliberately - merging preserves transactions, balance
 * anchors, statements and holdings, whereas deleting would discard them.
 */
export function useDuplicateAccounts() {
  return useQuery({
    queryKey: accountKeys.duplicates(),
    queryFn: () => accountsService.getDuplicates(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single account by ID
 */
export function useAccount(id: string) {
  return useQuery({
    queryKey: accountKeys.detail(id),
    queryFn: () => accountsService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch balance summary
 */
export function useBalanceSummary() {
  return useQuery({
    queryKey: accountKeys.balanceSummary(),
    queryFn: () => accountsService.getBalanceSummary(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to sync all accounts
 */
export function useSyncAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountsService.syncAll(),
    onSuccess: () => {
      // Invalidate accounts data to refetch after sync
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

/**
 * Hook to sync a specific account
 */
export function useSyncAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountsService.syncAccount(id),
    onSuccess: (_data, id) => {
      // Invalidate specific account and lists
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Hook to toggle account visibility
 */
export function useToggleAccountHidden() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isHidden }: { id: string; isHidden: boolean }) =>
      accountsService.setHidden(id, isHidden),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Hook to update account name
 */
export function useUpdateAccountName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      accountsService.updateName(id, name),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

/**
 * Hook to hide or unhide an account (WP-BUG-62).
 *
 * Before this hook existed, core's accounts.is_hidden (what GET /v1/accounts
 * filters on) was unreachable from the web UI, so an account that became
 * hidden could never be unhidden by the user.
 *
 * Writes through accountsService.setVisibility: a connected account goes
 * through the aggregator, which owns its flag and propagates it to core; a
 * manual account goes to core (Codex review on PR #8).
 */
export function useSetAccountVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ account, isHidden }: { account: AccountVisibilityTarget; isHidden: boolean }) =>
      accountsService.setVisibility(account, isHidden),
    onSuccess: (_data, { account }) => {
      const { id, sourceType } = account;
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      // The connection settings modal shows the same flag. Its cache is keyed
      // by AGGREGATOR connection id, which the account (holding core's
      // financial_connections id) does not know, so refresh every detail.
      if (sourceType !== 'manual') {
        queryClient.invalidateQueries({ queryKey: connectionKeys.details() });
      }
      // Invalidate every accounts list variant so both the default list and
      // the include-hidden list reflect the change, along with hiddenCount.
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Helper to group accounts by type
 */
export function groupAccountsByType(accounts: AccountWithConnection[]) {
  const grouped = new Map<string, AccountWithConnection[]>();

  // Use backend account types (not subtypes)
  // Order: assets first (depository → investment → real_estate → other_asset → insurance),
  // then liabilities (credit → loan → other_liability), then catch-all (other)
  const typeOrder = [
    'depository',
    'investment',
    'real_estate',
    'other_asset',
    'insurance',
    'credit',
    'loan',
    'other_liability',
    'other',
  ];

  // Initialize with empty arrays in the correct order
  typeOrder.forEach(type => grouped.set(type, []));

  // Group accounts
  accounts.forEach(account => {
    const type = account.type;
    const group = grouped.get(type) || [];
    group.push(account);
    grouped.set(type, group);
  });

  // Filter out empty groups and return as array of tuples
  return Array.from(grouped.entries()).filter(([_, accounts]) => accounts.length > 0);
}

// ============================================================================
// Manual Account Hooks
// ============================================================================

/**
 * Hook to create a manual account
 */
export function useCreateManualAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ManualAccountCreateRequest) => accountsService.createManual(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

/**
 * Hook to update a manual account
 */
export function useUpdateManualAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ManualAccountUpdateRequest }) =>
      accountsService.updateManual(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Hook to add a balance entry
 */
export function useAddBalanceEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, data }: { accountId: string; data: BalanceEntryCreateRequest }) =>
      accountsService.addBalanceEntry(accountId, data),
    onSuccess: (_data, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceHistory(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Hook to get balance history for an account
 */
export function useBalanceHistory(
  accountId: string,
  params?: { startDate?: string; endDate?: string; source?: string; limit?: number; offset?: number }
) {
  return useQuery({
    queryKey: ['balance-history', accountId, params],
    queryFn: () => accountsService.getBalanceHistoryPaginated(accountId, params),
    enabled: !!accountId,
  });
}

/**
 * Hook to delete a balance entry
 */
export function useDeleteBalanceEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, entryId }: { accountId: string; entryId: string }) =>
      accountsService.deleteBalanceEntry(accountId, entryId),
    onSuccess: (_data, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceHistory(accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Hook to merge two accounts
 * Transfers all child records from source to target, then soft-deletes source.
 */
export function useMergeAccounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceAccountId,
      targetAccountId,
    }: {
      sourceAccountId: string;
      targetAccountId: string;
    }): Promise<AccountMergeResponse> =>
      accountsService.mergeAccounts(sourceAccountId, targetAccountId),
    onSuccess: () => {
      // Invalidate all account data since merge affects multiple accounts
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

/**
 * Hook to close a manual account
 */
export function useCloseAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data?: { closingDate?: string; closingBalance?: number };
    }) => accountsService.closeAccount(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}

/**
 * Hook to delete an orphaned account
 * Only available for accounts whose connection is disconnected or deleted.
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountsService.deleteAccount(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.balanceSummary() });
    },
  });
}
